import { Card, Table, Tag } from "antd";
import { get } from "lodash";

const getPriceDisplay = (prices) => {
  if (!prices) return { value: 0, label: "-" };

  const type = get(prices, "type", "flat");
  const unit = get(prices, "unit", "eur");
  const suffix = unit === "pmss" ? "% PMSS" : "€";

  if (type === "flat") {
    const monthly = get(prices, "monthly", 0);
    return monthly
      ? { value: monthly, label: `${monthly} ${suffix}/mois` }
      : { value: 0, label: "-" };
  }

  const base = get(prices, "base", {});
  const values = Object.values(base).filter((v) => v && v > 0);
  if (values.length === 0) return { value: 0, label: "-" };

  const min = Math.min(...values);
  const max = Math.max(...values);

  if (min === max) {
    return { value: min, label: `${min} ${suffix}/mois` };
  }

  return {
    value: (min + max) / 2,
    label: `${min} - ${max} ${suffix}/mois`,
  };
};

const getCoverageTotal = (coverage) => {
  const covPricingMode = get(coverage, "pricingMode", "PACKAGED");

  if (covPricingMode === "PACKAGED") {
    return getPriceDisplay(get(coverage, "pricer.prices")).value;
  }

  const mappings = get(coverage, "coverageMappings", []);
  return mappings.reduce(
    (sum, m) => sum + getPriceDisplay(get(m, "pricer.prices")).value,
    0,
  );
};

const getCoveragePriceLabel = (coverage) => {
  const covPricingMode = get(coverage, "pricingMode", "PACKAGED");

  if (covPricingMode === "PACKAGED") {
    return getPriceDisplay(get(coverage, "pricer.prices")).label;
  }

  const total = getCoverageTotal(coverage);
  return total > 0 ? `${total.toFixed(2)} €` : "-";
};

const PricingSummary = ({ coverages }) => {
  const totalPlanPrice = coverages.reduce(
    (sum, cov) => sum + getCoverageTotal(cov),
    0,
  );

  const dataSource = coverages.map((coverage) => {
    const category = get(coverage, "coverageCategory", {});
    const covPricingMode = get(coverage, "pricingMode", "PACKAGED");
    const mappings = get(coverage, "coverageMappings", []);

    return {
      key: coverage.id,
      name: category.label || category.value,
      icon: category.icon,
      pricingMode: covPricingMode,
      priceLabel: getCoveragePriceLabel(coverage),
      benefits: mappings.length,
      children:
        covPricingMode === "COMPOSABLE"
          ? mappings.map((m) => ({
              key: m.id,
              name: `${get(m, "coverage.type", "")}${get(m, "coverage.subtype") ? ` - ${get(m, "coverage.subtype")}` : ""}`,
              pricingMode: null,
              priceLabel: getPriceDisplay(get(m, "pricer.prices")).label,
              benefits: null,
            }))
          : undefined,
    };
  });

  return (
    <Card title="Détail de la tarification" className="m-bottom-16">
      <Table
        dataSource={dataSource}
        pagination={false}
        size="small"
        columns={[
          {
            title: "Garantie / Prestation",
            dataIndex: "name",
            render: (name, record) => (
              <div className="d-flex" style={{ gap: "8px", alignItems: "center" }}>
                {record.icon && (
                  <img
                    src={record.icon}
                    alt=""
                    style={{ width: 20, height: 20 }}
                  />
                )}
                <span style={{ fontWeight: record.children ? 600 : 400 }}>
                  {name}
                </span>
              </div>
            ),
          },
          {
            title: "Mode",
            dataIndex: "pricingMode",
            width: 120,
            render: (mode) =>
              mode ? (
                <Tag color={mode === "COMPOSABLE" ? "green" : "blue"}>
                  {mode === "COMPOSABLE" ? "Composable" : "Forfaitaire"}
                </Tag>
              ) : null,
          },
          {
            title: "Prestations",
            dataIndex: "benefits",
            width: 80,
            render: (count) => (count != null ? count : null),
          },
          {
            title: "Prix",
            dataIndex: "priceLabel",
            width: 160,
            render: (label) => (
              <span
                style={{
                  fontWeight: 600,
                  color: label !== "-" ? "#16A34A" : "#64748B",
                }}
              >
                {label}
              </span>
            ),
          },
        ]}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell colSpan={3}>
              <strong>Prix total du plan</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell>
              <strong
                style={{
                  fontSize: 16,
                  color: totalPlanPrice > 0 ? "#16A34A" : "#64748B",
                }}
              >
                {totalPlanPrice > 0 ? `${totalPlanPrice.toFixed(2)} €/mois` : "-"}
              </strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </Card>
  );
};

export default PricingSummary;
